import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { reilmAPI } from '../../services/apiClient'
import { buildComputePayload } from './computePayload'

export const FLIP_COMPUTE_DEBOUNCE_MS = 300

// Live `computed` for the scenario on screen. Behaviour (debounce, stale responses, which errors
// show) is README.md ("Live computed values").
export default function useFlipCompute(propertyId, scenario, { enabled = true, delay = FLIP_COMPUTE_DEBOUNCE_MS } = {}) {
  const [computed, setComputed] = useState(undefined)
  // Which scenario (`_id`) and type `computed` was calculated for, so the page never shows one
  // scenario's figures under another (decision D9 and its review follow-up).
  // `input`: the body it was calculated from, so a field can tell whether a stale `computed` still
  // answers for its own current value (letter 99, 8.1).
  const [computedFor, setComputedFor] = useState({ id: undefined, type: undefined, input: undefined })
  const [loading, setLoading] = useState(false)
  // `{ id }` of the scenario whose request failed; only that scenario reports it (a chip switch clears it).
  const [error, setError] = useState(null)
  // The request key whose response (success or error) was last applied. Differs from the current
  // key from the moment the scenario changes -- debounce wait included, which `loading` does not
  // cover -- until that request settles.
  const [settledKey, setSettledKey] = useState(null)
  // Bumped by `retry`: sends the same inputs again (a failed compute is otherwise only retried by an edit).
  const [retryTick, setRetryTick] = useState(0)

  // A string, so an unrelated re-render (same scenario, new object identity or not) is a no-op dep.
  const body = useMemo(() => JSON.stringify(buildComputePayload(scenario)), [scenario])
  const scenarioId = scenario?._id ?? null
  // The scenario id is part of the key: two scenarios with identical inputs are still two requests.
  const currentKey = `${propertyId}|${scenarioId}|${body}`

  const lastSentRef = useRef(null)
  const seqRef = useRef(0)
  const controllerRef = useRef(null)

  useEffect(() => {
    if (!enabled || !propertyId) return undefined
    const key = currentKey
    if (key === lastSentRef.current) return undefined

    // The first request of a screen load goes out at once; only edits wait for the pause.
    const wait = lastSentRef.current == null ? 0 : delay
    const timer = setTimeout(() => {
      lastSentRef.current = key
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      const seq = ++seqRef.current
      setLoading(true)
      reilmAPI
        .computeFlip(propertyId, JSON.parse(body), { signal: controller.signal })
        .then(({ data }) => {
          if (seq !== seqRef.current) return
          setComputed(data?.data?.computed)
          const input = JSON.parse(body)
          setComputedFor({ id: scenarioId, type: input.scenarioType, input })
          setError(null)
          setSettledKey(key)
        })
        .catch((err) => {
          if (seq !== seqRef.current) return
          setSettledKey(key)
          const status = err?.response?.status
          const code = err?.response?.data?.error?.code
          // 401: the interceptor refreshes or signs out. Locked 403: the interceptor has already
          // dispatched reilm:subscription-expired and ExpiredModal says why. Neither gets a second message.
          if (status === 401) return
          if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) return
          // A 400 names the fields it refused (details.fields), so the page can say which input.
          const fields = status === 400 && code === 'VALIDATION_ERROR' ? err?.response?.data?.error?.details?.fields : null
          setError({ id: scenarioId, fields: Array.isArray(fields) ? fields : null })
        })
        .finally(() => {
          if (seq === seqRef.current) setLoading(false)
        })
    }, wait)
    return () => clearTimeout(timer)
  }, [enabled, propertyId, scenarioId, body, currentKey, delay, retryTick])

  useEffect(
    () => () => {
      seqRef.current += 1
      controllerRef.current?.abort()
    },
    []
  )

  // At once, and `pending` from this moment, so a caller waiting on the result sees it as out.
  const retry = useCallback(() => {
    lastSentRef.current = null
    setSettledKey(null)
    setRetryTick((t) => t + 1)
  }, [])

  // `pending`: the values in `computed` are not (yet) for the scenario on screen.
  const pending = Boolean(enabled && propertyId) && settledKey !== currentKey
  const ownError = error && error.id === scenarioId ? error : null
  return {
    computed,
    computedId: computedFor.id,
    computedType: computedFor.type,
    computedInput: computedFor.input,
    loading,
    error: ownError ? 'compute_failed' : null,
    errorFields: ownError?.fields ?? null,
    pending,
    retry,
  }
}
